/* ==========================================================================
   NOSH SHARED VIDEO SYSTEM                      [2026-08-21 CX + Ad Readiness]
   ==========================================================================
   ONE customer-facing watch experience for every genuine Nosh video:
   the homepage "From the Nosh community" cards, the homepage Founder's
   Favourites panels, and the Muskaan / Shanay founder collection heroes.

   Two responsibilities, deliberately kept separate:

     1. LAZY INLINE PREVIEWS.  Any <video data-nosh-vpreview> ships with NO
        <source> at all — the real URL sits in data-nosh-vsrc. The source is
        attached only when the element is genuinely near the viewport, and
        playback starts only when it is actually on screen, the tab is
        visible, no lightbox is open and the visitor does not prefer reduced
        motion. Before that the poster is all that loads. This is what takes
        the homepage's Community video payload off the initial load.

     2. THE VIEWER.  A single lazily-built <dialog> reused by every opener on
        the page, so there is exactly one player and one set of controls in
        the document no matter how many videos a page has.

   AUDIO RULE: previews are always muted; the viewer always opens UNMUTED,
   because it can only ever open from a deliberate tap or click. We never
   autoplay anything with sound.

   Openers are declared entirely in markup:

       <button data-nosh-video-open
               data-nosh-video-src='[{"src":"...","type":"video/mp4"}]'
               data-nosh-video-poster="https://..."
               data-nosh-video-label="Founder video: Muskaan">

   No third-party library. Native <dialog> gives us the focus trap,
   Escape-to-close and focus restoration for free.

   [2026-10-05 Founder refinements] OPTIONAL SHOPPABLE SLOT (additive).
   An opener may also carry  data-nosh-video-extra="<template id>".  The
   viewer then clones that <template> into a .nosh-vlb__extra slot under the
   label and adds .nosh-vlb--shop to the dialog (styled by the section that
   owns the template, e.g. the Mystery Box collection). Both are removed on
   close. Openers WITHOUT the attribute behave exactly as before. A caller
   that adds to cart inside the slot closes the viewer itself with
   NoshVideo.close({ restoreFocus: false }), so the cart drawer it opens is
   not robbed of focus.
   ========================================================================== */
(function () {
  'use strict';

  var VERSION = 1;
  if (window.NoshVideo && window.NoshVideo.version === VERSION) {
    window.NoshVideo.refresh();
    return;
  }

  function noop() {}

  function reducedMotion() {
    return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }

  function parseSources(raw) {
    if (!raw) return [];
    try {
      var parsed = JSON.parse(raw);
      if (Object.prototype.toString.call(parsed) === '[object Array]') return parsed;
    } catch (e) {
      /* A bare URL is also accepted so callers never have to build JSON. */
      return [{ src: raw, type: 'video/mp4' }];
    }
    return [];
  }

  /* ------------------------------------------------------------------ */
  /* 1. THE SHARED VIEWER                                                */
  /* ------------------------------------------------------------------ */

  var dialog = null;
  var player = null;
  var titleEl = null;
  var innerEl = null;
  var extraEl = null;
  var skipFocusRestore = false;
  var lastFocus = null;

  function buildViewer() {
    if (dialog) return dialog;

    dialog = document.createElement('dialog');
    dialog.className = 'nosh-vlb';
    dialog.setAttribute('aria-label', 'Video player');

    var inner = document.createElement('div');
    inner.className = 'nosh-vlb__inner';

    var close = document.createElement('button');
    close.type = 'button';
    close.className = 'nosh-vlb__close';
    close.setAttribute('aria-label', 'Close video');
    close.innerHTML =
      '<svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true" focusable="false">' +
      '<path d="M6 6l12 12M18 6L6 18" stroke="currentColor" stroke-width="2.2" stroke-linecap="round"/></svg>';
    close.addEventListener('click', function () {
      closeViewer();
    });

    player = document.createElement('video');
    player.className = 'nosh-vlb__video';
    player.setAttribute('controls', '');
    player.setAttribute('playsinline', '');
    player.setAttribute('preload', 'none');
    player.setAttribute('controlslist', 'nodownload');

    titleEl = document.createElement('p');
    titleEl.className = 'nosh-vlb__label';

    inner.appendChild(close);
    inner.appendChild(player);
    inner.appendChild(titleEl);
    innerEl = inner;
    dialog.appendChild(inner);
    document.body.appendChild(dialog);

    /* Escape (native `cancel`) and any other close path both land here. */
    dialog.addEventListener('close', teardownPlayback);
    dialog.addEventListener('cancel', function () {
      /* let the browser close it; `close` does the cleanup */
    });

    /* Click on the backdrop — i.e. on the dialog itself, outside .inner. */
    dialog.addEventListener('click', function (event) {
      if (event.target === dialog) closeViewer();
    });

    return dialog;
  }

  /* Stop playback AND stop the network. Setting removeAttribute('src') +
     load() is the only reliable way to make a browser abandon an in-flight
     media download; pause() alone leaves audio buffering on some engines. */
  function teardownPlayback() {
    if (!player) return;
    try {
      player.pause();
      player.currentTime = 0;
    } catch (e) {}
    while (player.firstChild) player.removeChild(player.firstChild);
    player.removeAttribute('src');
    player.removeAttribute('poster');
    try {
      player.load();
    } catch (e) {}
    document.documentElement.classList.remove('nosh-vlb-open');
    clearExtra();
    syncPreviews();
    if (skipFocusRestore) {
      skipFocusRestore = false;
      lastFocus = null;
      return;
    }
    if (lastFocus && typeof lastFocus.focus === 'function') {
      try {
        lastFocus.focus({ preventScroll: true });
      } catch (e) {
        lastFocus.focus();
      }
    }
    lastFocus = null;
  }

  /* The optional shoppable slot. Removed on every close path. */
  function clearExtra() {
    if (extraEl && extraEl.parentNode) extraEl.parentNode.removeChild(extraEl);
    extraEl = null;
    if (dialog) dialog.classList.remove('nosh-vlb--shop');
  }

  function mountExtra(templateId) {
    clearExtra();
    if (!templateId || !innerEl) return;
    var tpl = document.getElementById(templateId);
    if (!tpl || !tpl.content) return;
    extraEl = document.createElement('div');
    extraEl.className = 'nosh-vlb__extra';
    extraEl.appendChild(tpl.content.cloneNode(true));
    innerEl.appendChild(extraEl);
    dialog.classList.add('nosh-vlb--shop');
  }

  function closeViewer(opts) {
    if (!dialog) return;
    if (opts && opts.restoreFocus === false && dialog.open) skipFocusRestore = true;
    if (typeof dialog.close === 'function' && dialog.open) {
      dialog.close();
    } else {
      dialog.removeAttribute('open');
      teardownPlayback();
    }
  }

  function openViewer(opts) {
    var sources = opts.sources || [];
    if (!sources.length) return;

    buildViewer();
    lastFocus = document.activeElement;

    while (player.firstChild) player.removeChild(player.firstChild);
    for (var i = 0; i < sources.length; i++) {
      var s = document.createElement('source');
      s.src = sources[i].src;
      if (sources[i].type) s.type = sources[i].type;
      player.appendChild(s);
    }
    if (opts.poster) player.setAttribute('poster', opts.poster);

    var label = opts.label || 'Video';
    dialog.setAttribute('aria-label', label);
    titleEl.textContent = label;
    mountExtra(opts.extra || '');

    /* Deliberate user action opened this, so sound is expected and allowed. */
    player.muted = false;
    player.volume = 1;
    player.setAttribute('preload', 'auto');
    try {
      player.load();
    } catch (e) {}

    document.documentElement.classList.add('nosh-vlb-open');

    if (typeof dialog.showModal === 'function') {
      if (!dialog.open) dialog.showModal();
    } else {
      dialog.setAttribute('open', '');
    }

    /* AFTER the dialog is open, not before: viewerOpen() has to be true for
       syncPreviews() to pause the inline previews. Calling it first left
       every card preview playing behind the lightbox. */
    syncPreviews();

    var attempt = player.play();
    if (attempt && typeof attempt.catch === 'function') {
      attempt.catch(function () {
        /* A browser may still refuse unmuted autoplay (notably older iOS
           when the gesture is not trusted). Fall back to a muted start so
           the customer at least sees the video, and leave the native
           unmute control right there for them. */
        player.muted = true;
        var retry = player.play();
        if (retry && typeof retry.catch === 'function') retry.catch(noop);
      });
    }
  }

  function viewerOpen() {
    return !!(dialog && dialog.open);
  }

  /* ------------------------------------------------------------------ */
  /* 2. LAZY INLINE PREVIEWS                                             */
  /* ------------------------------------------------------------------ */

  var previews = [];
  var nearObserver = null;
  var playObserver = null;

  /* Attach the real media only once, and only when it is worth it. */
  function activate(video) {
    if (video.dataset.noshVactive === '1') return;
    var raw = video.getAttribute('data-nosh-vsrc');
    var sources = parseSources(raw);
    if (!sources.length) return;
    video.dataset.noshVactive = '1';
    for (var i = 0; i < sources.length; i++) {
      var s = document.createElement('source');
      s.src = sources[i].src;
      if (sources[i].type) s.type = sources[i].type;
      video.appendChild(s);
    }
    video.setAttribute('preload', 'metadata');
    try {
      video.load();
    } catch (e) {}

    /* A card that is ALREADY on screen when its source lands must start now.
       Without this it stays on the poster until the next scroll or resize
       fires a sync, which looks broken to anyone who lands mid-page. */
    syncPreviews();
  }

  function onScreen(el) {
    var r = el.getBoundingClientRect();
    if (r.height === 0 || r.width === 0) return false;
    var top = Math.max(r.top, 0);
    var bottom = Math.min(r.bottom, window.innerHeight || 0);
    return bottom - top >= r.height * 0.4;
  }

  function syncPreviews() {
    var allow = !reducedMotion() && !document.hidden && !viewerOpen();
    for (var i = 0; i < previews.length; i++) {
      var v = previews[i];
      if (!v.isConnected) continue;
      var want = allow && v.dataset.noshVactive === '1' && onScreen(v);
      if (want) {
        if (v.paused) {
          v.muted = true; /* previews are ALWAYS silent */
          var a = v.play();
          if (a && typeof a.catch === 'function') a.catch(noop);
        }
      } else if (!v.paused) {
        v.pause();
      }
    }
  }

  function observePreview(video) {
    if (video.dataset.noshVbound === '1') return;
    video.dataset.noshVbound = '1';
    video.muted = true;
    previews.push(video);

    if (nearObserver) {
      nearObserver.observe(video);
      playObserver.observe(video);
    } else {
      /* No IntersectionObserver: attach immediately rather than never. */
      activate(video);
    }
  }

  function buildObservers() {
    if (!('IntersectionObserver' in window)) return;

    /* Generous margin: start fetching just before the customer arrives so
       the preview is ready, without paying for it on initial load. */
    nearObserver = new IntersectionObserver(
      function (entries) {
        for (var i = 0; i < entries.length; i++) {
          if (entries[i].isIntersecting) {
            activate(entries[i].target);
            nearObserver.unobserve(entries[i].target);
          }
        }
      },
      { rootMargin: '400px 0px' }
    );

    playObserver = new IntersectionObserver(syncPreviews, { threshold: [0, 0.4, 0.8] });
  }

  /* ------------------------------------------------------------------ */
  /* 3. OPENERS                                                          */
  /* ------------------------------------------------------------------ */

  function onOpenerActivate(event) {
    var el = event.currentTarget;
    event.preventDefault();
    openViewer({
      sources: parseSources(el.getAttribute('data-nosh-video-src')),
      poster: el.getAttribute('data-nosh-video-poster') || '',
      label: el.getAttribute('data-nosh-video-label') || 'Video',
      extra: el.getAttribute('data-nosh-video-extra') || ''
    });
  }

  function bindOpener(el) {
    if (el.dataset.noshVopenBound === '1') return;
    el.dataset.noshVopenBound = '1';
    el.addEventListener('click', onOpenerActivate);
  }

  /* ------------------------------------------------------------------ */

  function refresh() {
    var opens = document.querySelectorAll('[data-nosh-video-open]');
    for (var i = 0; i < opens.length; i++) bindOpener(opens[i]);

    var vids = document.querySelectorAll('video[data-nosh-vpreview]');
    for (var j = 0; j < vids.length; j++) observePreview(vids[j]);

    /* Drop anything the theme editor has removed. */
    for (var k = previews.length - 1; k >= 0; k--) {
      if (!previews[k].isConnected) previews.splice(k, 1);
    }
    syncPreviews();
  }

  var ticking = false;
  function requestSync() {
    if (ticking) return;
    ticking = true;
    var run = function () {
      ticking = false;
      syncPreviews();
    };
    if (window.requestAnimationFrame) window.requestAnimationFrame(run);
    else window.setTimeout(run, 66);
  }

  function init() {
    buildObservers();
    refresh();
  }

  window.addEventListener('scroll', requestSync, { passive: true });
  window.addEventListener('resize', requestSync);
  document.addEventListener('visibilitychange', syncPreviews);
  document.addEventListener('shopify:section:load', refresh);
  document.addEventListener('shopify:section:unload', function () {
    window.setTimeout(refresh, 0);
  });

  window.NoshVideo = {
    version: VERSION,
    open: openViewer,
    close: closeViewer,
    refresh: refresh
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
